import React, { useCallback } from 'react'

import { useLocation, useRouteMatch } from 'react-router-dom'

import { find, get } from 'lodash'
import moment from 'moment'
import qs from 'qs'

import { Button, Col, PageHeader, Row, Typography } from 'antd'

import { observer } from 'mobx-react-lite'

import { Box, FlexBoxX } from 'common/components/boxes'
import { Card } from 'common/components/Card'
import { Comments } from 'common/components/comments'
import { Loading } from 'common/components/Loading'
import { Page } from 'common/components/Page'
import OrderState from 'common/components/statuses/order_state'
import UnloadWarning from 'common/components/unload_warning'
import { Visibility } from 'common/components/Visibility'
import { getOrderTitleType } from 'common/helpers/get_order_type_title'
import { useQuery } from 'common/hooks/use-query'
import { useTheme } from 'common/hooks/use-theme'
import { OrderStates } from 'common/server/server_types'
import UploaderStore from 'common/stores/UploaderStore'

import { Attachments } from 'vendor/components/Attachments/attachments'
import { OrderActions } from 'vendor/components/OrderActions/order_actions'
import { OrderInfo } from 'vendor/components/OrderInfo/order_info'
import { QuoteExpiration } from 'vendor/components/QuoteExpiration'
import { RequestedCancellation } from 'vendor/components/RequestedCancellation'
import { useOrderAsyncAction, StateChanges } from 'vendor/hooks/use-order-async-action'
import { useOrderNewRelicUserInfo } from 'vendor/hooks/use-order-new-relic-user-info'
import { usePublicVendorStores } from 'vendor/hooks/use-public-stores'

import { Materials } from './materials'

interface OrderDetailProps {
  companyVendorContactId: string
}

const OrderDetail = observer<OrderDetailProps>(({ companyVendorContactId }) => {
  const { orderStore, uploaderStore, unitsStore } = usePublicVendorStores()
  const theme = useTheme()
  const { params } = useRouteMatch<{ id: string }>()
  const { search } = useLocation()
  const { vendor_contact_email } = qs.parse(search, { ignoreQueryPrefix: true })

  const updateAction = (stateChanges: StateChanges, uploaderStore: UploaderStore) => {
    return orderStore.updateOrder(
      companyVendorContactId,
      stateChanges,
      uploaderStore.signedIds('vendor_files'),
      uploaderStore.deleteAttachmentIds['vendor_files'],
      get(uploaderStore.signedIds('quote'), '[0]'),
    )
  }

  const { isSubmitting, execute } = useOrderAsyncAction(updateAction, orderStore.selectedOrder)

  const { isLoading } = useQuery(() => orderStore.selectOrder(params.id))
  useQuery(unitsStore.maybeUnits)
  useOrderNewRelicUserInfo(orderStore.selectedOrder)

  const goToVendorLogin = useCallback(() => {
    window.location.assign(`/vendor/order/${params.id}`)
  }, [params])

  if (isLoading || !orderStore.selectedOrder) {
    return <Loading />
  }

  const { state, sub_state, order_package_name, comments, vendor_contacts } = orderStore.selectedOrder

  const currentContact = (() => find(vendor_contacts, (contact) => contact.email === vendor_contact_email))()

  const orderTypeTitle = getOrderTitleType(state)

  const handleUpdate = (stateChanges: StateChanges) => {
    execute(stateChanges, uploaderStore)
  }

  const handleConfirmCancel = async () => {
    const successMesasge = 'Order cancelled!'
    const errorMessage = 'Unable to cancel order'
    await execute(
      { cancellation_accepted_at: moment().toISOString(), cancellation_rejected_at: null },
      uploaderStore,
      successMesasge,
      errorMessage,
    )
  }

  const handleRejectCancel = async () => {
    const successMesasge = 'Rejected order cancelling!'
    const errorMessage = 'Unable to reject cancelling order'
    await execute(
      { cancellation_accepted_at: null, cancellation_rejected_at: moment().toISOString() },
      uploaderStore,
      successMesasge,
      errorMessage,
    )
  }

  const quoteExpirationProps = {
    quoteScheduledToExpireAt: orderStore.selectedOrder.quote_scheduled_to_expire_at,
    defaultQuoteValidForDays: orderStore.selectedOrder?.company_vendor?.vendor?.default_quote_valid_for_days,
    onChange: (date: string) => {
      orderStore.selectedOrder.quote_scheduled_to_expire_at = date
      orderStore.setOrderDirty(true)
    },
    onSetDefaultExpiration: (date: string) => {
      orderStore.selectedOrder.quote_scheduled_to_expire_at = date
    },
  }

  return (
    <Box height="100%">
      <Box bg={theme.colors.primary} width="100%">
        <FlexBoxX justifyContent="flex-start" mr={16}>
          <FlexBoxX flexGrow={1} justifyContent="flex-start">
            <img
              src="/subbase_logo_short.png"
              style={{ width: '42px', height: '42px', marginLeft: '8px', marginRight: '8px' }}
              alt="subbase-logo"
            />
            <Typography.Title level={3} style={{ margin: 0, color: theme.colors.white }}>
              SubBase
            </Typography.Title>
          </FlexBoxX>
          {currentContact?.vendor_user &&
            (currentContact?.vendor_user?.invitation_accepted ? (
              <Button onClick={goToVendorLogin}>Login</Button>
            ) : (
              <Button onClick={() => window.location.assign(currentContact?.vendor_user?.invitation_link)}>
                Register
              </Button>
            ))}
        </FlexBoxX>
      </Box>

      <Page height="calc(100% - 42px)">
        <Page.Header>
          <PageHeader
            title={
              <Box
                display="flex"
                alignItems={{ _: 'flex-start', md: 'center' }}
                flexDirection={{ _: 'column', md: 'row' }}
              >
                <Box display="flex" alignItems="center" mb={2}>
                  <Typography.Text style={{ marginRight: 20 }}>{orderTypeTitle}</Typography.Text>
                  <Typography.Text type="secondary" style={{ fontSize: 16 }}>
                    {order_package_name}
                  </Typography.Text>
                </Box>

                <Box ml={{ _: 0, md: 20 }} display="flex" alignItems="center">
                  <OrderState
                    state={state}
                    sub_state={sub_state}
                    showIssue
                    deliveryIssues={orderStore?.selectedOrder?.deliveries?.map((d) => d.delivery_issue_type)}
                    size="medium"
                    texts={{
                      ORDERED: {
                        ORDERED_REQUESTED: 'Order Placed',
                      },
                    }}
                  />

                  {state === OrderStates.QUOTED && (
                    <Visibility.Show>
                      <QuoteExpiration {...quoteExpirationProps} />
                    </Visibility.Show>
                  )}
                </Box>
              </Box>
            }
            style={{ padding: '0', width: '100%' }}
            extra={[
              ...(state === OrderStates.QUOTED
                ? [
                    <Visibility.Hidden key="quote-expire">
                      <QuoteExpiration {...quoteExpirationProps} />
                    </Visibility.Hidden>,
                  ]
                : []),
              <Visibility.Hidden breakpoint="md" key="actions">
                <OrderActions
                  order={orderStore.selectedOrder}
                  orderDirty={orderStore.selectedOrderDirty}
                  updateOrder={handleUpdate}
                  isSubmitting={isSubmitting}
                />
              </Visibility.Hidden>,
            ]}
          />
        </Page.Header>

        <Page.Content p={0} px={{ _: 0, sm: 16 }} pt={16} pb={{ _: 100, md: 32 }} overflowX="hidden">
          <RequestedCancellation
            cancellationRequestedAt={orderStore.selectedOrder?.cancellation_requested_at}
            cancellationAcceptedAt={orderStore.selectedOrder?.cancellation_accepted_at}
            cancellationRejectedAt={orderStore.selectedOrder?.cancellation_rejected_at}
            cancellationRequestedReason={orderStore.selectedOrder?.cancellation_requested_reason}
            onConfirmCancel={handleConfirmCancel}
            onRejectCancel={handleRejectCancel}
            isSubmitting={isSubmitting}
          />

          <Row gutter={[16, 16]}>
            <Col span={24}>
              <OrderInfo order={orderStore.selectedOrder} />
            </Col>

            <Col span={24}>
              <Materials />
            </Col>

            <Col xs={24} lg={12} xxl={10}>
              <Attachments
                order={orderStore.selectedOrder}
                onChange={(isDirty: boolean) => (orderStore.selectedOrderDirty = isDirty)}
                addExistingFiles={uploaderStore.addExistingFiles}
                resetAllUploads={uploaderStore.resetAllUploads}
                onRemoveUpload={uploaderStore.removeUpload}
                onAddNewUpload={uploaderStore.addNewUpload}
                onResetUploads={uploaderStore.resetUploads}
                onSetUploadError={uploaderStore.setUploadError}
                onUpdateUpload={uploaderStore.updateUpload}
                fileList={uploaderStore.fileList}
                accept=".pdf,application/pdf"
              />
            </Col>

            <Col xs={24} lg={12} xxl={14}>
              <Card title="Messages" minHeight="256px">
                <Comments
                  comments={comments}
                  onChangeNewComment={orderStore.updateNewComment}
                  newComment={orderStore.newComment}
                  currentView="VENDOR"
                />
              </Card>
            </Col>
          </Row>
        </Page.Content>

        <Visibility.Show breakpoint="md">
          <OrderActions
            order={orderStore.selectedOrder}
            orderDirty={orderStore.selectedOrderDirty}
            updateOrder={handleUpdate}
            isSubmitting={isSubmitting}
            isFloat
          />
        </Visibility.Show>

        <UnloadWarning showWarning={() => orderStore.selectedOrderDirty} />
      </Page>
    </Box>
  )
})

export default OrderDetail
